import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

interface MultiSelectFilterDropdownProps {
  isOpen: boolean;
  toggle: () => void;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel: string;
  formatOption?: (value: string) => string;
  width?: number | string;
  className?: string;
  requireConfirm?: boolean;
}

export const MultiSelectFilterDropdown = ({
  isOpen,
  toggle,
  options,
  selected,
  onChange,
  allLabel,
  formatOption = (value) => value,
  width = 150,
  className = "",
  requireConfirm = false,
}: MultiSelectFilterDropdownProps) => {
  const normalizeValue = (value: string) => String(value).trim();
  const normalizedSelected = useMemo(
    () => selected.map((item) => normalizeValue(item)),
    [selected]
  );
  const [draftSelected, setDraftSelected] = useState<string[]>(normalizedSelected);

  useEffect(() => {
    if (!isOpen) {
      setDraftSelected(normalizedSelected);
    }
  }, [isOpen, normalizedSelected]);

  const isSelected = (value: string) =>
    draftSelected.some((item) => normalizeValue(item) === normalizeValue(value));

  const toggleValue = (value: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const normalizedValue = normalizeValue(value);
    const nextSelected = isSelected(normalizedValue)
      ? draftSelected.filter(
          (item) => normalizeValue(item) !== normalizedValue
        )
      : [...draftSelected, normalizedValue];

    setDraftSelected(nextSelected);

    if (!requireConfirm) {
      onChange(nextSelected);
    } else {
    }
  };

  const clearAll = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftSelected([]);
    if (!requireConfirm) {
      onChange([]);
    }
  };

  const buttonLabel =
    draftSelected.length === 0
      ? allLabel
      : draftSelected.length === 1
      ? formatOption(normalizeValue(draftSelected[0]))
      : `${draftSelected.length} selected`;

  const applySelection = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(draftSelected);
    toggle();
  };

  const clearAndClose = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftSelected([]);
    onChange([]);
    toggle();
  };

  const handleToggle = () => {
    if (isOpen || !requireConfirm) {
      setDraftSelected(normalizedSelected);
    } else {
      setDraftSelected(normalizedSelected);
    }
    toggle();
  };

  return (
    <Dropdown isOpen={isOpen} toggle={handleToggle}>
      <DropdownToggle
        tag="button"
        className={`btn btn-light d-flex align-items-center ${className}`}
        style={{ width }}
      >
        <span className="text-truncate" style={{ minWidth: 0 }}>
          {buttonLabel}
        </span>
        <i className="mdi mdi-chevron-down flex-shrink-0 ms-1" />
      </DropdownToggle>
      <DropdownMenu
        style={{ maxHeight: "15rem", zIndex: 1, minWidth: width }}
      >
        <div style={{ overflowY: "auto", maxHeight: "11rem" }}>
          <DropdownItem toggle={false} onClick={clearAll}>
            <span className="text-muted">{allLabel}</span>
          </DropdownItem>
          <DropdownItem divider />
          {options.map((option) => {
            const normalizedOption = normalizeValue(option);
            const checked = isSelected(normalizedOption);
            return (
            <DropdownItem
              key={normalizedOption}
              toggle={false}
              onClick={(event) => toggleValue(normalizedOption, event)}
              className="d-flex align-items-center gap-2"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {}}
                onClick={(event) => event.stopPropagation()}
                className="form-check-input me-2"
                style={{ pointerEvents: "none" }}
              />
              <span>{formatOption(normalizedOption)}</span>
            </DropdownItem>
          );
          })}
        </div>
        {requireConfirm && (
          <div className="border-top d-flex gap-2 p-2 bg-white position-sticky bottom-0">
            <Button
              color="light"
              size="sm"
              className="flex-fill"
              onClick={clearAndClose}
            >
              Clear
            </Button>
            <Button
              color="primary"
              size="sm"
              className="flex-fill"
              onClick={applySelection}
            >
              Confirm
            </Button>
          </div>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
